'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { LuPanelLeftClose, LuPanelLeftOpen, LuUserRound } from 'react-icons/lu'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'
import { useHydratedStore } from '@/core/utils/zustand-persist'

import Logo from '@/features/veluvit/components/shell/logo'
import {
  GemIcon /* , TicketIcon — [2차 개발] 해금권 */,
} from '@/features/veluvit/components/ui/currency-icon'
import GemCount from '@/features/veluvit/components/ui/gem-count'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { useAppNow } from '@/features/veluvit/hooks/use-app-now'
import { useRecentRooms } from '@/features/veluvit/hooks/use-recent-rooms'
import { ago } from '@/features/veluvit/lib/home'
import { to } from '@/features/veluvit/lib/route'
import { openAuth } from '@/features/veluvit/services/account.actions'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

/** 펼친 폭 / 접은 폭 — app-shell 이 같은 값으로 본문 · 상단 막대를 오른쪽으로 민다(--side-w) */
export const SIDE_W = { open: 256, closed: 68 }

/**
 * 접고 펼칠 때 글자가 다시 줄바꿈되며 늘어지지 않게 — 안쪽은 늘 펼친 폭(256)으로 고정하고, 바깥 패널 폭만 바뀌며 잘라 보인다.
 * 얼굴 · 아이콘은 접은 폭(68)의 가운데(왼쪽에서 18px)에 오도록 줄 여백을 맞춰 두어 접어도 제자리에 있고, 글자만 옅어지며 사라진다.
 */
const FADE = 'whitespace-nowrap transition-opacity duration-150'

/**
 * 대화방 한 줄 — 메신저식(얼굴 · 이름 + 시간 · 마지막 말). 높이 · 여백은 여기 하나로(cn 이 병합하지 않으니 쓰는 쪽에서 겹쳐 주지 않는다).
 * 얼굴 가운데 = 접은 폭(68)의 가운데: 목록 여백 8 + 줄 왼쪽 6 + 얼굴 반 20 = 34.
 * 2026-10-07 "양옆이 타이트" — 줄 오른쪽 여백을 4 → 12 로(시각 · 말줄임이 판 끝에 붙어 있었다). 얼굴과 글 사이도 12 그대로
 */
const ROW =
  'flex h-16 items-center gap-3 rounded-panel pr-3 pl-1.5 no-underline transition-colors duration-150 aria-[current=page]:bg-foreground/10'

/**
 * PC 왼쪽 패널(1001px~) — Claude · ChatGPT 웹처럼 **대화방 목록** 자리. 로고 · 대화방들 · 계정(로그인)을 세로로 두고, 본문은 그 오른쪽을 꽉 채운다.
 * 메뉴(홈 · 랭킹 …)는 오른쪽 위 막대(gnb)에 그대로 둔다. 맨 위 버튼으로 얼굴만 남는 줄(68px)로 접고 펼친다(기기에 남김).
 * 패널 폭 · 본문 위치 · 위 막대 위치가 같은 시간(240ms) · 같은 곡선으로 함께 움직인다(app-shell --side-w).
 */
export default function SideNav() {
  const now = useAppNow()
  const pathname = usePathname()
  const collapsed = useHydratedStore(useUiStore, (s) => s.sideCollapsed, false)
  const setCollapsed = useUiStore((s) => s.setSideCollapsed)
  const { catalog, thumb } = useCatalogMedia()
  const account = useSessionStore((state) => state.data?.user)
  const rooms = useRecentRooms()
  const user = account && !account.guest ? account : null
  const hide = collapsed
    ? 'pointer-events-none opacity-0'
    : 'opacity-100 delay-75'

  return (
    <aside
      aria-label="대화 목록"
      className="absolute inset-y-0 left-0 z-[60] hidden w-(--side-w) overflow-hidden bg-well transition-[width] duration-[240ms] ease-smooth over10:block"
    >
      <div className="flex h-full w-64 flex-col">
        {/* 머리 — 접기 버튼은 늘 같은 자리(왼쪽), 로고는 그 옆에서 옅어진다 */}
        <div className="flex h-14.5 flex-none items-center gap-1 px-4">
          <Button
            type="button"
            aria-label={collapsed ? '대화 목록 펼치기' : '대화 목록 접기'}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed(!collapsed)}
            className="grid size-9 flex-none cursor-pointer place-items-center rounded-field text-t3 transition-colors hover:bg-foreground/[0.07] hover:text-t1"
            variant="bare"
          >
            {collapsed ? (
              <LuPanelLeftOpen aria-hidden="true" className="size-5" />
            ) : (
              <LuPanelLeftClose aria-hidden="true" className="size-5" />
            )}
          </Button>
          <Link
            href="/"
            aria-label="홈"
            tabIndex={collapsed ? -1 : 0}
            className={cn('ml-1 no-underline', FADE, hide)}
          >
            <Logo className="text-c22" />
          </Link>
        </div>

        {/* 대화방 목록 — ChatGPT 의 대화 목록 자리(최근 대화한 순). 접으면 얼굴만 남는다 */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-none px-2 pt-2">
          {/* 머리글 '대화' — 줄 안 얼굴 왼쪽 끝(8 + 6)에 맞춘다 */}
          <p
            className={cn(
              'mb-1 h-7 px-1.5 text-c13 leading-7 font-semibold text-t3',
              FADE,
              hide
            )}
          >
            대화
          </p>
          {rooms.length ? (
            // grid 는 칸 폭이 내용에 맞춰 늘어나 긴 제목이 패널 밖으로 밀려났다 — 세로 flex 로 폭을 패널에 고정한다
            <ul className="flex flex-col gap-0.5">
              {rooms.map((room) => {
                const c = catalog.chars[room.workId]
                // 대화방 주소는 방 id(chat-route) — 같은 작품의 다른 방이 함께 켜지지 않는다
                const on = pathname === `/chat/${room.id}`
                const name = room.title || c.name
                const last = room.messages.at(-1)?.content
                return (
                  <li key={room.id} className="min-w-0">
                    <Link
                      href={to(`/chat/${room.id}`)}
                      title={collapsed ? name : undefined}
                      data-tip-side="right"
                      aria-current={on ? 'page' : undefined}
                      className={cn(ROW, !on && 'hover:bg-foreground/[0.06]')}
                    >
                      <span className="relative size-10 flex-none overflow-hidden rounded-full">
                        <SafeImage
                          src={thumb(room.workId)}
                          alt=""
                          fill
                          sizes="40px"
                          className="object-cover object-[50%_20%]"
                          appearance="plain"
                        />
                      </span>
                      <span className={cn('block min-w-0 flex-1', FADE, hide)}>
                        <span className="flex items-baseline gap-2">
                          <span className="min-w-0 flex-1 truncate text-c14h font-semibold text-t1">
                            {name}
                          </span>
                          <span className="flex-none text-c12 text-t4">
                            {ago(room.updatedAt, now)}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-c13 text-t3">
                          {last || '대화를 시작해 보세요'}
                        </span>
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p
              className={cn(
                'px-2 py-3 text-c14 leading-[1.6] text-t3',
                FADE,
                hide
              )}
            >
              아직 나눈 대화가 없어요.
              <br />
              <Link
                href="/explore"
                tabIndex={collapsed ? -1 : 0}
                className="font-semibold text-t1 underline-offset-4 hover:underline"
              >
                캐릭터 둘러보기
              </Link>
            </p>
          )}
        </div>

        {/* 아래 — 계정 줄. 사람 아이콘은 접어도 제자리, 글자만 옅어진다. 게스트는 누르면 로그인 창 */}
        <div className="flex-none p-2.5">
          {user ? (
            <Link
              href="/my"
              title={collapsed ? '마이' : undefined}
              data-tip-side="right"
              aria-current={pathname === '/my' ? 'page' : undefined}
              className="flex h-14 items-center gap-2.5 rounded-panel px-1.5 no-underline transition-colors hover:bg-foreground/[0.06] aria-[current=page]:bg-foreground/10"
            >
              <span className="grid size-9 flex-none place-items-center rounded-full bg-foreground/10 text-t1">
                <LuUserRound aria-hidden="true" className="size-4.5" />
              </span>
              <span className={cn('min-w-0 flex-1', FADE, hide)}>
                <span className="block truncate text-c14h font-bold text-t1">
                  {user.name}
                </span>
                <span className="mt-0.5 flex items-center gap-2.5 text-c13 text-t3 tabular-nums">
                  <span className="inline-flex items-center gap-1">
                    <GemIcon />
                    <GemCount />
                  </span>
                  {/* [2차 개발] 해금권 — 2026-10-08 2차 범위로 넘김. 되살릴 땐 이 블록 · TicketIcon import · tickets 주석을 푼다
                  <span className="inline-flex items-center gap-1">
                    <TicketIcon />
                    {tickets}
                  </span> */}
                </span>
              </span>
            </Link>
          ) : (
            <Button
              type="button"
              title={collapsed ? '로그인' : undefined}
              data-tip-side="right"
              onClick={() => openAuth('login')}
              className="flex h-14 w-full cursor-pointer items-center gap-2.5 rounded-panel px-1.5 text-left transition-colors hover:bg-foreground/[0.06]"
              variant="bare"
            >
              <span className="grid size-9 flex-none place-items-center rounded-full bg-foreground text-background">
                <LuUserRound aria-hidden="true" className="size-4.5" />
              </span>
              <span className={cn('min-w-0 flex-1', FADE, hide)}>
                <span className="block text-c14h font-bold text-t1">
                  로그인 · 가입
                </span>
                <span className="block truncate text-c13 text-t3">
                  대화 기록을 계정에 남겨요
                </span>
              </span>
            </Button>
          )}
        </div>
      </div>
    </aside>
  )
}
